(() => {
  const rootSelector = '[data-threedi-hero-guard]';
  const initializedKey = 'threediHeroOverlapGuard';

  if (document.documentElement.dataset[initializedKey] === 'true') return;
  document.documentElement.dataset[initializedKey] = 'true';

  let frame = null;
  const pendingScopes = new Set();

  const findRoots = (scope) => {
    if (!(scope instanceof Element) && scope !== document) return [];

    const roots = scope.matches?.(rootSelector) ? [scope] : [];
    return roots.concat(Array.from(scope.querySelectorAll?.(rootSelector) || []));
  };

  const resetRoot = (root, fields) => {
    root.classList.remove('is-threedi-hero-flow');
    root.style.removeProperty('--threedi-hero-guard-start');
    fields.forEach((field) => field.style.removeProperty('--threedi-hero-guard-gap-before'));
  };

  const measureRoot = (root) => {
    const stage = root.querySelector('.threedi-hero-field-stage') || root;
    const group = stage?.querySelector('.threedi-hero-field-group');

    if (!stage || !group) {
      root.classList.remove('is-threedi-hero-flow');
      root.style.removeProperty('--threedi-hero-guard-start');
      return;
    }

    const fields = Array.from(group.querySelectorAll('[data-threedi-hero-field]'));
    resetRoot(root, fields);
    if (fields.length < 2) return;

    const items = fields.map((field) => ({
      field,
      position: field.querySelector('.threedi-hero-field-position') || field,
    }));

    if (items.length < 2) return;

    const gapValue = Number.parseFloat(getComputedStyle(root).getPropertyValue('--threedi-hero-guard-gap'));
    const minimumGap = Number.isFinite(gapValue) ? Math.max(0, gapValue) : 16;
    const measurements = items.map((item) => ({
      top: item.position.offsetTop,
      height: item.position.offsetHeight,
    }));

    const hasCollision = measurements.some((measurement, index) => {
      if (index === 0) return false;
      const previous = measurements[index - 1];
      return measurement.top < previous.top + previous.height + minimumGap;
    });
    const finalMeasurement = measurements[measurements.length - 1];
    const exceedsStage = finalMeasurement.top + finalMeasurement.height + minimumGap > stage.clientHeight;

    if (!hasCollision && !exceedsStage) return;

    root.style.setProperty('--threedi-hero-guard-start', `${Math.max(0, measurements[0].top)}px`);
    items.forEach((item, index) => {
      if (index === 0) return;
      const previous = measurements[index - 1];
      const configuredGap = measurements[index].top - (previous.top + previous.height);
      item.field.style.setProperty('--threedi-hero-guard-gap-before', `${Math.max(minimumGap, configuredGap)}px`);
    });
    root.classList.add('is-threedi-hero-flow');
  };

  const run = () => {
    const scopes = pendingScopes.size ? Array.from(pendingScopes) : [document];
    pendingScopes.clear();
    frame = null;

    const roots = new Set();
    scopes.forEach((scope) => findRoots(scope).forEach((root) => roots.add(root)));
    roots.forEach(measureRoot);
  };

  const schedule = (scope = document) => {
    pendingScopes.add(scope);
    if (frame) return;
    frame = requestAnimationFrame(run);
  };

  const start = () => {
    schedule();
    window.addEventListener('load', () => schedule(), { once: true });
    window.addEventListener('resize', () => schedule());
    window.addEventListener('pageshow', () => schedule());
    document.addEventListener('shopify:section:load', (event) => schedule(event.target));
    document.addEventListener('shopify:section:select', () => schedule());
    document.addEventListener('shopify:block:select', () => schedule());
    document.fonts?.ready.then(() => schedule()).catch(() => {});
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
